import React, {useContext, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';

import {useShell} from './Shell';
import LogoImage from '../images/logo.svg';
import Searchbar from './Searchbar';
import {Button} from '@mui/material';
import {AuthContext} from '../App';
import {$post} from '../lib/requests';
import {Link, useNavigate} from 'react-router-dom';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
`;

const SiteTitle = styled.div`
  font-family: 'odia', 'Roboto', sans-serif;
  font-size: 28px;
  white-space: nowrap;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  width: 232px;

  .logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
  }
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  margin: 8px;
`;

const SettingsIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  position: relative;
`;

const SettingsPopup = styled.div<{open?: boolean}>`
  padding: 20px;
  border-radius: 8px;
  background: white;
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  border: 1px rgba(0, 0, 0, 0.1) solid;
  box-shadow: 0 0 8px rgba(32, 33, 36, 0.2);
  z-index: 100;

  display: ${props => (props.open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 12px;
  min-width: 150px;
`;

const PopupLink = styled(Link)`
  text-decoration: none;
  color: #1a73e8;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const LogoutButton = styled(Button)`
  && {
    margin-top: 8px;
  }
`;

const UserDropdownBtn = styled.button`
  border: 2px black solid;
  border-radius: 18px;
  width: 36px;
  height: 36px;
  background: white;
  padding: 0;
  overflow: hidden;
  cursor: pointer;

  img {
    width: 36px;
    height: 36px;
    margin: 0;
  }
`;

export default function Header({
  toolbar = <Searchbar />,
}: {
  toolbar?: React.ReactNode;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const shellApi = useShell();
  const settingsPopupRef = useRef<HTMLDivElement>(null);
  const {auth, setAuth} = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        e.target instanceof Node &&
        settingsPopupRef.current &&
        !settingsPopupRef.current.contains(e.target)
      ) {
        setSettingsOpen(false);
      }
    }
    document.body.addEventListener('mousedown', handleOutsideClick);

    return () =>
      document.body.removeEventListener('mousedown', handleOutsideClick);
  }, [settingsPopupRef]);

  function toggleLeftSidebar() {
    if (shellApi.getState().isLeftSidebarOpen) {
      shellApi.setLeftSidebarOpen(false);
    } else {
      shellApi.setLeftSidebarOpen(true);
    }
  }

  function openSettingsPopup() {
    setSettingsOpen(x => !x);
  }

  async function handleLogout() {
    await $post('/logout');
    if (setAuth) setAuth(null);
    navigate('/login');
  }

  return (
    <Toolbar>
      <LogoContainer>
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="Toggle navigation menu"
          onClick={toggleLeftSidebar}
        >
          <MenuIcon />
        </IconButton>
        <Link to="/" className="logo-link">
          <Logo src={LogoImage} alt="logo" width="32px" height="32px" />
          <SiteTitle>ପାଠାଗାର</SiteTitle>
        </Link>
      </LogoContainer>
      {toolbar}
      <SettingsIconContainer ref={settingsPopupRef}>
        <UserDropdownBtn
          aria-label="User menu"
          aria-haspopup="true"
          aria-expanded={settingsOpen}
          onClick={openSettingsPopup}
        >
          {auth?.user && <img src={auth.user.picture} alt={auth.user.name} />}
        </UserDropdownBtn>
        <SettingsPopup open={settingsOpen}>
          {auth?.isAdmin && (
            <PopupLink to="/admin" onClick={() => setSettingsOpen(false)}>
              Admin Dashboard
            </PopupLink>
          )}
          {auth?.user?.role === 'VOLUNTEER' && (
            <PopupLink to="/volunteer" onClick={() => setSettingsOpen(false)}>
              My Assignments
            </PopupLink>
          )}
          <LogoutButton onClick={handleLogout} variant="outlined" size="small">
            logout
          </LogoutButton>
        </SettingsPopup>
      </SettingsIconContainer>
    </Toolbar>
  );
}
